/**
 * Progressive layout *model* for Sprocket's IDE shell.
 *
 * This file owns the pure description of "what panes go where" for each
 * curriculum stage. It is deliberately DOM-free: a `Stage` maps to a tree of
 * `SplitNode`/`PaneNode` values, which `layout-view.ts` then renders to real
 * elements. Keeping the model pure lets us assert the stage → layout mapping
 * exhaustively in node-environment unit tests (no jsdom), mirroring the rest of
 * the scaffold.
 *
 * The panes here are *placeholders* — labelled empty boxes. The real surfaces
 * land in downstream tasks and slot in by pane id:
 *   - "tutor"     → lesson / walkthrough panel  (#702)
 *   - "immediate" → immediate-mode REPL         (#701)
 *   - "editor"    → Monaco code editor          (#710)
 *   - "files"     → file tree                    (#710)
 *   - "canvas"    → turtle / graphics surface    (Stage 3, future — see seam below)
 *
 * Terminology note: the briefing's "horizontal split" means a *horizontal
 * divider* (panes stacked top/bottom); "vertical split" means a *vertical
 * divider* (panes side by side left/right). `layout-view.ts` maps those to the
 * matching flexbox direction.
 */

/** Curriculum stage that drives the layout. Grows over time (Stage 3 = turtle). */
export type Stage = 1 | 2;

/** Divider orientation of a split, named for the divider line itself. */
export type Orientation = "horizontal" | "vertical";

/** Stable identifiers for every placeholder pane the shell can host. */
export type PaneId = "tutor" | "immediate" | "files" | "editor" | "canvas";

/** Human-readable placeholder titles, shown until real content is mounted. */
export const PANE_LABELS: Record<PaneId, string> = {
  tutor: "Tutor",
  immediate: "Immediate",
  files: "Files",
  editor: "Editor",
  canvas: "Canvas",
};

/** A leaf region of the layout — a single labelled placeholder pane. */
export interface PaneNode {
  kind: "pane";
  id: PaneId;
  label: string;
  /** Relative flex weight within its parent split (default 1). */
  weight: number;
}

/** An internal node — two-or-more children separated by draggable resizers. */
export interface SplitNode {
  kind: "split";
  orientation: Orientation;
  children: LayoutNode[];
  /** Relative flex weight within its parent split, if nested (default 1). */
  weight: number;
}

export type LayoutNode = PaneNode | SplitNode;

/** Build a placeholder pane node. */
function pane(id: PaneId, weight = 1): PaneNode {
  return { kind: "pane", id, label: PANE_LABELS[id], weight };
}

/** Build a split node from its orientation and children. */
function split(
  orientation: Orientation,
  children: LayoutNode[],
  weight = 1,
): SplitNode {
  return { kind: "split", orientation, children, weight };
}

/**
 * Map a curriculum stage to its layout tree.
 *
 * Stage 1 — a single horizontal split: Tutor (top) over Immediate (bottom).
 * Stage 2 — a vertical split: the Files+Editor column (left) beside a
 *           horizontal split of Tutor (top) over Immediate (bottom) (right).
 */
export function layoutForStage(stage: Stage): LayoutNode {
  switch (stage) {
    case 1:
      // Tutor gets the lion's share; the immediate window is a stout footer.
      return split("horizontal", [pane("tutor", 2), pane("immediate", 1)]);

    case 2:
      return split("vertical", [
        // Left column: a narrow file tree beside the editor.
        split("vertical", [pane("files", 1), pane("editor", 3)], 3),
        // Right column: the Stage 1 tutor/immediate stack, now beside the editor.
        split("horizontal", [pane("tutor", 2), pane("immediate", 1)], 2),
      ]);

    // Stage 3 seam: a turtle/graphics canvas joins here. The intended shape is
    // to wrap the Stage 2 tree and the "canvas" pane in an outer split (e.g.
    // editor column | canvas | tutor/immediate column). Intentionally NOT built
    // yet — #7xx owns it. The `default` keeps this total without a runtime hole.
    default:
      return layoutForStage(1);
  }
}

/** Depth-first collect every leaf pane, left-to-right / top-to-bottom. */
export function collectPanes(node: LayoutNode): PaneNode[] {
  if (node.kind === "pane") return [node];
  return node.children.flatMap(collectPanes);
}
